import styled from '@emotion/styled';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import P2 from '@base/P2/P2';
import useForm from '@base/Form/useForm';
import Field from '@base/Form/Field';
import Button from '@base/Button/Button';
import Link from '@base/Link/Link';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import fetcher from '@api/fetchers/simpleFetcher';
import { segment } from '@actions/analytics';
import { getCMSHost } from '@utils/api-helper/host';
import { useSnackbar } from '@stores/snackbar';
import Dropdown from '@comp/Dropdown/Dropdown';
import NumberField from '@base/NumberField/NumberField';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import { PREDICT_NOW_CLICKED } from '@constants/segment';
import { useEffect, useState } from 'react';
import { VALIDATIONS } from '../constant';
import { AVAILABLE_SLOTS } from './constants';
import { isRequired, scoreWithinRange, isFloat } from '../utils';

const Title = styled(H2)`
  margin-bottom: ${Spacings.SPACING_3B};
`;

const EditorWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  margin-bottom: ${Spacings.SPACING_10B};

  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_6B};
  }
`;

const StyledH6 = styled(H6)`
  margin-bottom: ${Spacings.SPACING_4B};
`;

const StyledNumberField = styled(NumberField)`
  width: 100%;
`;

export const StyledButton = styled(Button)`
  ${({ $colorType }) =>
    $colorType === 'light_dark' && {
      color: 'var(--color-i-white)',
      backgroundColor: 'var(--color-i-black)',
      marginRight: `${Spacings.SPACING_4B}`
    }}
  &:active {
    background-color: ${({ $colorType }) =>
      $colorType === 'light_dark' && 'var(--color-i-black)'};
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const Error = styled(P2)`
  font-weight: 600;
  color: var(--color-i-red);
  margin-top: ${Spacings.SPACING_2B};
`;

const FieldWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyledSpan = styled.div`
  font-size: ${Fonts.P2};
  color: var(--color-text-primary);
  font-weight: ${Fonts.LIGHT};
  margin-bottom: ${Spacings.SPACING_5B};
  a {
    text-decoration: underline;
    color: var(--color-text-primary);
  }
`;

const renderErrorMessage = ({ message }) => {
  return <Error>{message}</Error>;
};

const Form = ({
  initialData,
  setPredictedPercentile,
  setShowResult,
  predictedPercentile
}) => {
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { open: showSnackbar } = useSnackbar();
  const { registrar, isSubmitting, onFormSubmit } = useForm({
    data: initialData
  });
  const defaultCMSHost = getCMSHost();
  const [selectedSlot, setSelectedSlot] = useState(AVAILABLE_SLOTS[0]);

  useEffect(() => {
    if (isLoggedIn && predictedPercentile) {
      setShowResult(true);
    }
  }, [isLoggedIn, predictedPercentile, setShowResult]);

  const onSubmit = async (data) => {
    segment.track(PREDICT_NOW_CLICKED, {
      goal_uid: 'XNDUS'
    });
    try {
      const response = await fetcher(
        `${defaultCMSHost}api/cat-rank-predictor?verbal=${data?.VARC}&quant=${data?.QA}&lrdi=${data?.DILR}&slot=${selectedSlot.value}`
      );
      if (response.error) {
        showSnackbar({
          type: 'SET_SNACKBAR_MESSAGE',
          message: 'Error fetching percentile. Please try later',
          messageType: 'error'
        });
      } else {
        setPredictedPercentile(response);
      }
      if (!isLoggedIn) {
        openLoginDrawer();
      }
    } catch (error) {
      showSnackbar({
        type: 'SET_SNACKBAR_MESSAGE',
        message: 'Error fetching percentile. Please try later',
        messageType: 'error'
      });
    }
  };
  const handleOnSubmit = onFormSubmit(onSubmit);

  return (
    <>
      <Title>Enter your CAT 2025 Marks</Title>
      <P1>to receive your percentile</P1>
      <EditorWrapper>
        <StyledH6>Expected Marks in VARC</StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="VARC"
            rules={[
              isFloat('Please enter a valid score'),
              isRequired('Score is required'),
              scoreWithinRange('Please enter a valid score', -24, 72)
            ]}
            render={({ onChange, value, hasError }) => (
              <StyledNumberField
                allowNegative
                allowFloat
                placeholder="Enter your score out of 72"
                value={value}
                onChange={(_, data) => onChange(data)}
                error={hasError}
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>

        <StyledH6>Expected Marks in DILR</StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="DILR"
            rules={[
              isFloat('Please enter a valid score'),
              isRequired('Score is required'),
              scoreWithinRange('Please enter a valid score', -20, 66)
            ]}
            render={({ onChange, value, hasError }) => (
              <StyledNumberField
                placeholder="Enter your score out of 66"
                value={value}
                onChange={(_, data) => onChange(data)}
                error={hasError}
                inputProps={{
                  maxLength: VALIDATIONS.maxLength
                }}
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>
        <StyledH6>Expected Marks in Quantitative Ability (QA)</StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="QA"
            rules={[
              isFloat('Please enter a valid score'),
              isRequired('Score is required'),
              scoreWithinRange('Please enter a valid score', -22, 66)
            ]}
            render={({ onChange, value, hasError }) => (
              <StyledNumberField
                placeholder="Enter your score out of 66"
                value={value}
                onChange={(_, data) => onChange(data)}
                error={hasError}
                inputProps={{
                  maxLength: VALIDATIONS.maxLength
                }}
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>
        <StyledH6>Slot</StyledH6>
        <FieldWrapper>
          <Field
            registrar={registrar}
            name="slot"
            rules={[]}
            render={() => (
              <Dropdown
                options={AVAILABLE_SLOTS}
                selectedOption={selectedSlot}
                onOptionClick={setSelectedSlot}
                buttonWidth="100%"
                placeholder="Pick a slot"
              />
            )}
            renderError={renderErrorMessage}
          />
        </FieldWrapper>
      </EditorWrapper>
      <StyledSpan>
        By continuing, you agree to our{' '}
        <Link href="/rank-predictor-terms">Terms & Conditions</Link>
      </StyledSpan>
      <StyledButton
        label="Predict my Percentile"
        variant={isSubmitting ? 'disabled' : 'filled'}
        onClick={handleOnSubmit}
        $colorType="light_dark"
      />
    </>
  );
};

export default Form;
